import { AuthProviders } from '@iwentsourcing/core/authentication/auth-providers.js';
import { CustomError } from '@iwentsourcing/core/error/custom-error.js';
import { JwtSessionData } from '../auth/jwt-session-data.js';
import { AuthParams } from '../auth/auth-params.js';
import { buildJwtSessionData } from '../auth/build-jwt-session-data.js';
import { createJwtSessionHandler } from '../auth/create-jwt-session-handler.js';
import { createRpcClient } from '../api/create-rpc-client.js';
import { getOrCreate } from '@iwentsourcing/core/utils/get-or-create.js';
import { createSessionManager } from '../auth/jwt-session.js';
import { JwtPayload } from '@iwentsourcing/core/authentication/jwt/jwt-payload.js';
import { JwtSessionStorage } from '../auth/jwt-session-storage.js';
import { parseJwt } from '@iwentsourcing/core/authentication/jwt/parse-jwt.js';
import { OAuthTransferStateData } from './o-auth-transfer-state-data.js';
import {
  OAuthEventData,
  OAuthRemoteInterface,
} from './o-auth-remote-interface.js';
import { UnauthenticatedSession } from './unauthenticated-session.js';
import { AuthenticatedSession } from './authenticated-session.js';
import { OAuthOptions } from './o-auth-options.js';

import { AuthDeviceResponse } from '@iwentsourcing/core/authentication/oauth/auth-device-response.js';
import { createLogger } from '@iwentsourcing/core/logger/logger.js';
import { TokenAuthDefinitions } from '@iwentsourcing/core/authentication/token-auth-definitions.js';
import { OpenIdAuthDefinitions } from '@iwentsourcing/core/authentication/open-id-auth-definitions.js';
import { JwtAuthDefinitions } from '@iwentsourcing/core/authentication/jwt-auth-definitions.js';
import { BadRequestError } from '@iwentsourcing/core/error/bad-request-error.js';
import { createDelayedEmitter } from '@iwentsourcing/core/emitter/create-delayed-emitter.js';
import { EventAuth } from '@iwentsourcing/core/event-store/event-auth.js';
import { PublicAuthProvider } from '@iwentsourcing/core/authentication/public-auth-providers.js';
import { getAuthId } from '@iwentsourcing/core/authentication/get-auth-id.js';
import { RuntimeEnv } from '@iwentsourcing/core/environment/runtime-env.js';
import type { Lock } from '@iwentsourcing/core/lock/lock.js';
import { Lazy } from '@iwentsourcing/core/lazy/lazy.js';

interface OAuthPersistStorage {
  sessionStorage: JwtSessionStorage<JwtSessionData>;
  transferStorage: JwtSessionStorage<OAuthTransferStateData>;
}

async function loadTransferSession(
  realmName: string,
  params: AuthParams,
  options: OAuthPersistStorage,
): Promise<JwtSessionData | null> {
  if (typeof window !== 'undefined') {
    const searchParams = new URLSearchParams(
      window.location.search.substring(window.location.search.indexOf('?')),
    );
    const state = searchParams.get('state');
    const code = searchParams.get('code');

    if (!!state && !!code) {
      const transferState = await options.transferStorage.load();
      if (!transferState) {
        return await options.sessionStorage.load();
      }

      await options.transferStorage.clear();

      const api = createRpcClient({ url: params.url, env: params.env });
      const response = await api['auth.authorize'].call(
        {
          realm: transferState.realm,
          tenant: transferState.tenant,
          code,
          redirectUri: transferState.redirectUri,
          provider: transferState.provider,
        },
        params.signal,
      );
      const sessionData = buildJwtSessionData(
        realmName,
        params.tenant,
        transferState.provider,
        response,
      );
      await options.sessionStorage.save(sessionData);
      return sessionData;
    }
  }

  return await options.sessionStorage.load();
}

interface OAuthStorage extends OAuthPersistStorage {
  lock: Lock;
}

function createOAuthInterface<TAuth extends AuthProviders>(
  realmName: string,
  authProviders: Lazy<Record<string, PublicAuthProvider>>,
  params: AuthParams,
  opts: OAuthStorage,
): OAuthRemoteInterface<TAuth> {
  const emitter = createDelayedEmitter<
    UnauthenticatedSession | AuthenticatedSession
  >(
    (async () => {
      const sessionData = await loadTransferSession(realmName, params, opts);
      if (sessionData) {
        handleSession(sessionData);
      }
      return sessionToAuthState(params.env, sessionData);
    })(),
  );

  const logger = createLogger('oauth');

  const sessionManager = createSessionManager();
  const api = createRpcClient({ url: params.url, env: params.env });

  function handleSession(sessionData: JwtSessionData) {
    sessionManager.addSession((signal) =>
      createJwtSessionHandler(api, {
        data: sessionData,
        signal,
        sessionStorage: opts.sessionStorage,
        lock: opts.lock,
      }),
    );
  }

  async function updateSession(env: RuntimeEnv, sessionData: JwtSessionData) {
    await opts.sessionStorage.save(sessionData);

    sessionManager.clearSession();
    handleSession(sessionData);

    const currentSession = await sessionToAuthState(env, sessionData);
    emitter.emit(currentSession);
  }

  async function sessionToAuthState(
    env: RuntimeEnv,
    session: JwtSessionData | null,
  ): Promise<AuthenticatedSession | UnauthenticatedSession> {
    if (session) {
      const payload = parseJwt(session.accessToken, 1, JwtPayload);
      const eventAuth: EventAuth = {
        iss: payload.iss,
        aud: payload.aud,
        sub: payload.sub,
      };
      return {
        type: 'authenticated',
        authId: await getAuthId(env, eventAuth),
        auth: eventAuth,
        payload,
      };
    } else {
      return { type: 'unauthenticated' };
    }
  }

  async function logout() {
    await opts.sessionStorage.clear();
    await opts.transferStorage.clear();
    sessionManager.clearSession();
    emitter.emit({ type: 'unauthenticated' });
  }

  return {
    sessions: {
      resolveAuth(): Promise<EventAuth | undefined> {
        return sessionManager.resolveAuth();
      },
      async resolveToken(): Promise<string | undefined> {
        try {
          return await sessionManager.resolveToken();
        } catch (e) {
          if (e instanceof CustomError) {
            if (e.data['error'] === 'no session') {
              logout().catch((err) => {
                logger.error(err);
              });
            }
          } else if (e instanceof BadRequestError) {
            if (e.message === 'unknown session') {
              logout().catch((err) => {
                logger.error(err);
              });
            }
          }
          throw e;
        }
      },
    },
    on(cb: (evt: OAuthEventData) => void, signal: AbortSignal): void {
      emitter.on(cb, signal);
    },
    async login(
      provider: keyof JwtAuthDefinitions<TAuth> & string,
      username: string,
      password: string,
      signal: AbortSignal,
    ): Promise<void> {
      const providers = await authProviders.resolve();
      const config = providers[provider];

      if (!config) {
        throw new CustomError('unknown provider', {
          provider,
        });
      }

      const api = createRpcClient({ url: params.url, env: params.env });
      const response = await api['auth.login'].call(
        {
          tenant: params.tenant,
          realm: realmName,
          provider,
          username,
          password,
        },
        signal,
      );
      const sessionData = buildJwtSessionData(
        realmName,
        params.tenant,
        provider,
        response,
      );
      await updateSession(params.env, sessionData);
    },
    async register(
      provider: keyof JwtAuthDefinitions<TAuth> & string,
      username: string,
      password: string,
      signal: AbortSignal,
    ): Promise<void> {
      const providers = await authProviders.resolve();
      const config = providers[provider];

      if (!config) {
        throw new CustomError('unknown provider', {
          provider,
        });
      }

      const api = createRpcClient({ url: params.url, env: params.env });
      const response = await api['auth.register'].call(
        {
          tenant: params.tenant,
          realm: realmName,
          provider,
          username,
          password,
        },
        signal,
      );
      const sessionData = buildJwtSessionData(
        realmName,
        params.tenant,
        provider,
        response,
      );
      await updateSession(params.env, sessionData);
    },
    async *deviceLogin(
      provider: keyof OpenIdAuthDefinitions<TAuth> & string,
      signal: AbortSignal,
    ): AsyncGenerator<AuthDeviceResponse> {
      const providers = await authProviders.resolve();
      const config = providers[provider];

      if (!config) {
        throw new CustomError('unknown provider', {
          provider,
        });
      }

      const api = createRpcClient({ url: params.url, env: params.env });
      for await (const response of api['auth.deviceCode'].call(
        {
          tenant: params.tenant,
          realm: realmName,
          provider,
        },
        signal,
      )) {
        if (response.type === 'token') {
          const sessionData = buildJwtSessionData(
            realmName,
            params.tenant,
            provider,
            response.token,
          );
          await updateSession(params.env, sessionData);
        }
        yield response;
      }
    },
    async tokenLogin(
      provider: keyof TokenAuthDefinitions<TAuth> & string,
      token: string,
      signal: AbortSignal,
    ): Promise<void> {
      const providers = await authProviders.resolve();
      const config = providers[provider];

      if (!config) {
        throw new CustomError('unknown provider', {
          provider,
        });
      }

      const api = createRpcClient({ url: params.url, env: params.env });
      const response = await api['auth.token'].call(
        {
          tenant: params.tenant,
          realm: realmName,
          provider,
          token,
        },
        signal,
      );
      const sessionData = buildJwtSessionData(
        realmName,
        params.tenant,
        provider,
        response,
      );
      await updateSession(params.env, sessionData);
    },
    navigateToLogin: async (provider) => {
      const state = Math.random().toString(36);
      const providers = await authProviders.resolve();
      const config = providers[provider];

      if (!config) {
        throw new CustomError('unknown provider', {
          provider,
        });
      }

      if (config.type !== 'openid' && config.type !== 'token') {
        throw new CustomError('expected openid or token provider', {
          provider,
        });
      }

      if (!config.authorize) {
        throw new CustomError('authorized not supported', {
          provider,
        });
      }

      const qs = {
        ...config.authorize.queryParams,
        state,
        redirect_uri: window.location.origin + window.location.pathname,
      };

      await opts.transferStorage.save({
        redirectUri: qs.redirect_uri,
        provider: provider,
        tenant: params.tenant,
        realm: realmName,
      });

      window.location.href = `${config.authorize.url}?${Object.entries(qs)
        .map(([key, value]) => `${key}=${String(value)}`)
        .join('&')}`;
    },
    logout,
    async resolveAuth(): Promise<
      UnauthenticatedSession | AuthenticatedSession
    > {
      return await emitter.value();
    },
  };
}

export function createOAuthStorage<TAuth extends AuthProviders>(
  realmName: string,
  authProviders: Lazy<Record<string, PublicAuthProvider>>,
  opts: OAuthOptions,
): (params: AuthParams) => OAuthRemoteInterface<TAuth> {
  const interfaces: Record<string, OAuthRemoteInterface<any>> = {};

  return (params) => {
    // The interface (RPC client + session manager) is per tenant API
    // instance, so it is cached by tenant+realm+url.
    const interfaceKey = `${params.tenant}:${realmName}:${params.url}`;
    // Session and transfer state are keyed by realm (not tenant): one JWT
    // authenticates the user across every tenant of the realm, so a login
    // must be reusable by sibling tenant API instances.
    const realmKey = `${realmName}:${params.url}`;
    const sessionStorage = opts.sessionStorageFactory(
      `auth_session_${realmKey}`,
      JwtSessionData,
    );

    const transferStorage = opts.transferStorageFactory(
      `auth_transfer_${realmKey}`,
      OAuthTransferStateData,
    );
    return getOrCreate(interfaces, interfaceKey, () =>
      createOAuthInterface(realmName, authProviders, params, {
        sessionStorage,
        transferStorage,
        lock: opts.lock,
      }),
    );
  };
}
